import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseIndexPageComponent } from '../../../../components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from '../../../../components/pages/base-index-page/base-index-page.config';
import { DataTableColumnActions } from '../../../../components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import { DataTableColumnText } from '../../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import ApiOptionsConfig from '../../../../services/api/api-options.config';
import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { PLANNING_STATUS_LABELS, Planning, PlanningStatus, planningUserFullName } from '../../../../models/entities/planning';

@Component({
  selector: 'app-activities-planning-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './activities-planning-index-page.component.html',
  styleUrl: './activities-planning-index-page.component.scss',
})
export class ActivitiesPlanningIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;

  private activeProfileCode: string | null = null;
  private currentUserId: number | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    protected qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Planning',
      cardTitle: 'Plannings',
      dataTable: {
        data: [],
        searchModel: {
          code: null,
          dueDate: null,
          assigneeUserId: null,
          title: null,
          status: 'ACTIVE',
        },
        pagination: null,
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            value: (item: Planning) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'dueDate',
            header: 'Due Date',
            filter: 'date',
            value: (item: Planning) => this.qualityManagementService.formatPlanningDateTime(item?.dueDate)
          }),
          new DataTableColumnText({
            attribute: 'assigneeUserId',
            header: 'Assignee',
            filter: 'select',
            filterOptionsSource: 'planningAssignees',
            value: (item: Planning) => planningUserFullName(item?.assigneeUser) || '-'
          }),
          new DataTableColumnText({
            attribute: 'title',
            header: 'Title',
            filter: 'text',
            value: (item: Planning) => item?.title ?? ''
          }),
          new DataTableColumnText({
            attribute: 'status',
            header: 'Status',
            filter: 'select',
            filterOptionsSource: 'planningStatus',
            defaultFilterValue: 'ACTIVE',
            value: (item: Planning) => PLANNING_STATUS_LABELS[item?.status as PlanningStatus] ?? ''
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            style: { width: '90px' },
            buttons: [
              {
                icon: 'fa-search',
                tooltip: 'View planning details',
                onClick: (item: Planning) => {
                  this.routeService.activitiesPlanningDetail(Number(item.activityPlanningId));
                }
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete planning',
                onClick: () => { },
                isDelete: true,
                hidden: (item: Planning) => !this.canDeletePlanning(item)
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.qualityManagementService.listPlannings(options).pipe(
        map((response: any) => ({
          ...response,
          data: {
            activityPlannings: this.qualityManagementService.readResponseKey(response, 'activityPlannings') ?? []
          },
          pagination: response?.pagination ?? response?.data?.pagination ?? null
        }))
      ),
      responseListModelName: 'activityPlannings',
    }
  }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserId = user?.userId != null ? Number(user.userId) : null;
      });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.activeProfileCode = profileCode;

        if (this.accessControlService.hasFeatureActionAccess(profileCode, 'QUALITY_MANAGEMENT_ACTIVITIES_PLANNING', 'modify')) {
          this.baseIndexPageConfig.addNewItemButton = {
            type: 'success',
            title: 'New planning',
            onClick: () => this.routeService.activitiesPlanningCreate()
          };
        } else {
          this.baseIndexPageConfig.addNewItemButton = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private canDeletePlanning(item: Planning): boolean {
    const isCreator = this.currentUserId != null && Number(item?.creatorUser?.userId) === this.currentUserId;
    const hasDeleteAccess = this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_ACTIVITIES_PLANNING',
      'delete'
    );

    return isCreator || hasDeleteAccess;
  }
}
